import { useRef, useState } from 'react'
import { Editor, EditorContent } from '@tiptap/react'
import { sentenceOffsetsSelector } from '@chegg-wt/core-wa/state/selectors'
import { useSelector } from 'react-redux'
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '@chegg-wt/core/utils/localStorage'
import { EDITOR_WRAPPER_TEST_ID } from '@chegg-wt/core-wa/constants/paraphraser'
import { BubbleMenuContainer } from '../BubbleMenuContainer'
import {
  RewriteInfoTooltip,
  SynonymsInfoTooltip,
  STORAGE_KEY_FOR_REWRITE_SYNONYMS_INFO_TOOLTIP,
  REWRITE_SYNONYMS_VIEWED_STATE,
} from '../InfoTooltip'
import { EditorWrapperContainer } from './styled'

interface EditorWrapperProps {
  editor: Editor
  isPaperCheckInProgress: boolean
}

const EditorWrapper = ({ editor, isPaperCheckInProgress }: EditorWrapperProps) => {
  const sentenceOffset = useSelector(sentenceOffsetsSelector)
  /**
   * Logic for rewrite and synonyms info tooltips
   * Since there is no event handler by default from the editor, an click event
   * is bind on the parent container. This allows us to fidn the position of editor container
   * and determing the exact position of the user clicked. Exact position of the info tooltip
   * are calculated in the respective components SynonymsInfoTooltip, and RewriteInfoTooltip
   * setRewriteSynonymsView and getRewriteSynonymsViewed are shortcuts functions to write/read
   * the local storage for STORAGE_KEY_FOR_REWRITE_SYNONYMS_INFO_TOOLTIP
   * This key can have multiple values REWRITE, SYNONYMS, NONE
   * REWRITE means First user click and render rewrite info tooltip
   * SYNONYMS means Second user click and render synonyms info tooltip
   * NONE means user has viewed both info tools and no need to have store user track user click positions
   */
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const [userClickPosition, setUserClickPosition] = useState({ x: 0, y: 0 })

  const setRewriteSynonymsView = (value: REWRITE_SYNONYMS_VIEWED_STATE) =>
    setLocalStorageItem(STORAGE_KEY_FOR_REWRITE_SYNONYMS_INFO_TOOLTIP, value)

  const getRewriteSynonymsViewed = () => {
    if (sentenceOffset.length > 0 && !isPaperCheckInProgress) {
      return getLocalStorageItem(STORAGE_KEY_FOR_REWRITE_SYNONYMS_INFO_TOOLTIP)
    }
    return null
  }

  const isUserClickForRewriteFlash =
    getRewriteSynonymsViewed() === REWRITE_SYNONYMS_VIEWED_STATE.REWRITE
  const isUserClickForSynonyms =
    getRewriteSynonymsViewed() === REWRITE_SYNONYMS_VIEWED_STATE.SYNONYMS

  const handleClickOnEditor = (event: {
    clientX: number
    clientY: number
    stopPropagation: () => void
  }) => {
    event.stopPropagation()

    if (getRewriteSynonymsViewed() === REWRITE_SYNONYMS_VIEWED_STATE.NONE) {
      // do nothing if user already viewed synonyms flash message
      return
    }
    // Works only when user reviewed the paper
    if (sentenceOffset.length > 0 && !isPaperCheckInProgress) {
      setUserClickPosition({ x: event.clientX, y: event.clientY })

      if (isUserClickForSynonyms) {
        // When third user click
        setRewriteSynonymsView(REWRITE_SYNONYMS_VIEWED_STATE.NONE)
      }
      if (isUserClickForRewriteFlash) {
        // When second user click
        setRewriteSynonymsView(REWRITE_SYNONYMS_VIEWED_STATE.SYNONYMS)
      }
      if (getRewriteSynonymsViewed() === null) {
        // When First user clicks
        setRewriteSynonymsView(REWRITE_SYNONYMS_VIEWED_STATE.REWRITE)
      }
    }
  }

  return (
    <EditorWrapperContainer
      isEditorDisabled={isPaperCheckInProgress}
      ref={editorContainerRef}
      onClick={handleClickOnEditor}
      id="editor-container"
      data-test={`${EDITOR_WRAPPER_TEST_ID}`}
    >
      <BubbleMenuContainer editor={editor} />
      <EditorContent editor={editor} />

      {isUserClickForRewriteFlash && (
        <RewriteInfoTooltip
          userClickPosition={userClickPosition}
          parentRef={editorContainerRef}
        />
      )}
      {isUserClickForSynonyms && (
        <SynonymsInfoTooltip
          userClickPosition={userClickPosition}
          parentRef={editorContainerRef}
        />
      )}
    </EditorWrapperContainer>
  )
}

export default EditorWrapper
